import { useState, useSyncExternalStore } from 'react';
import Button from '@srm/ui/button';
import SupportBar from '@molecules/SupportBar';
import { sendNotification } from '@srm/ui/lib/notifications';
import { getSupportSession, subscribeSupportSession } from '@/auth/supportSession';
import {
    closeSupportSession,
    extendSupportSession,
    SupportExtendForbiddenError,
} from '@/auth/supportSessionClient';
import { useNow } from '@srm/ui/hooks/useNow';
import { formatSupportTimeLeft } from './formatSupportTimeLeft';

/**
 * Неубираемая полоса вкладки «от имени» (PLAN.md §7.3): чью учётку открыл сотрудник, по какому обращению,
 * сколько осталось; продление на 30 минут и завершение входа. Рамку окна и приставку заголовка рисует
 * `SupportSessionGate`.
 */
export default function SupportSessionBanner() {
    const session = useSyncExternalStore(subscribeSupportSession, getSupportSession);
    const now = useNow(session ? 15000 : null);
    const [closing, setClosing] = useState(false);
    const [extending, setExtending] = useState(false);
    // Сервер запретил продление (вход с согласия пользователя и т. п.): кнопка больше не предлагается.
    const [extendForbidden, setExtendForbidden] = useState(false);
    if (!session) return null;

    const extend = () => {
        setExtending(true);
        extendSupportSession()
            .catch((reason: unknown) => {
                if (reason instanceof SupportExtendForbiddenError) setExtendForbidden(true);
                sendNotification({
                    title: 'Вход не продлён',
                    message: reason instanceof Error ? reason.message : 'Не удалось продлить вход.',
                    variant: 'red',
                });
            })
            .finally(() => setExtending(false));
    };

    return (
        <SupportBar
            tone="control"
            person={session.actorName}
            title={`Вы вошли от имени ${session.userName}`}
            description={[session.ticketRef && `обращение ${session.ticketRef}`, formatSupportTimeLeft(session.expiresAt, now)]
                .filter(Boolean)
                .join(' · ')}
            label="Вход от имени пользователя"
            data-testid="support-session-bar"
            actions={
                <>
                    {!extendForbidden && (
                        <Button
                            size="sm"
                            variant="outline-neutral"
                            loading={extending}
                            disabled={closing}
                            onClick={extend}
                        >
                            Продлить на 30 мин
                        </Button>
                    )}
                    <Button
                        size="sm"
                        loading={closing}
                        onClick={() => {
                            setClosing(true);
                            // Локальный вход завершается и при сетевом отказе; серверный токен истечёт по сроку.
                            void closeSupportSession().catch(() => undefined);
                        }}
                    >
                        Завершить вход
                    </Button>
                </>
            }
        />
    );
}
